import { Children, useEffect, useState } from 'react';
import { node } from 'prop-types';
import { difference, find, isEmpty, map, partition, sortBy, without } from 'lodash';
import Toolbar from '@material-ui/core/Toolbar';
import { useSnackbar } from 'notistack';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { useLocation } from 'react-router-dom';

import { getLogger } from '../../../utilities/UseLog';
import useStyles from './FilterContainerStyles';
import { encodeState, useUrlState } from '../../../utilities/FilterStateUrlUtilities';
import { useFilterState } from '../../../stores/FilterStateStore';
import { useSavedFiltersService } from '../../../services/SavedFiltersService';
import Filter from '../filter/Filter';
import FilterMenu from './FilterMenu';
import PerformanceLog from '../../performance-log/PerformanceLog';
import { filterId as timeRangeFilterId } from '../../filters/time-range-filter/TimeRangeFilter';
import { useSaveFiltersDialog } from './SaveFiltersDialog';
import TimeRangeHistoryProvider from './TimeRangeHistoryProvider';
import { isDashboard } from '../dashboard/DashboardContext';
import { usePageId } from '../page/PageUtils';

const log = getLogger('FilterContainer');

function getEncodedFilterState(urlState, defaultState, dashboardSavedFilterState, navFromSidebar) {
    const defaultSavedFilterState = defaultState || dashboardSavedFilterState;

    // If there's a default saved/shared state, and the user navigated to the page using the
    // sidebar, use the default saved filter state.  This ensures the default saved/shared
    // state gets applied if this is not a drill-in with additional filters. Drill-in state
    // should always get applied, even if a default state exists.
    if (defaultSavedFilterState && (navFromSidebar || isEmpty(urlState))) {
        // Use the encoded state if it exists (new saved filters), or use the hydrated state if not (legacy saved filters).
        return defaultSavedFilterState.encodedFilterState || encodeState(defaultSavedFilterState.filterState);
    }

    return urlState;
}

function partitionFilters(filters, urlEncodedState) {
    function sortFilters(filter1, filter2) {
        // The time range filter should always be first.
        if (filter1.props.id === timeRangeFilterId) return -1;
        if (filter2.props.id === timeRangeFilterId) return 1;

        return 0;
    }

    // Get all filter options by filter ID.
    const filterOptions = filters.reduce((options, filter) => {
        return {
            ...options,
            [filter.props.id]: filter.props.options,
        };
    }, {});

    // Partition the filters into required and optional groups.
    const [requiredFilters, allOptionalFilters] = partition(filters, (filter) => filter.props.required);

    // Partition the optional filters into active and inactive.  Active filters are defined as having state from the URL.
    const [activeOptionalFilters, optionalFilters] = partition(sortBy(allOptionalFilters, 'props.title'), (filter) => {
        return urlEncodedState && urlEncodedState[filter.props.id] !== undefined;
    });

    // Active filters are the combination of required filters and those with state from the URL.
    const activeFilters = [...requiredFilters.sort(sortFilters), ...activeOptionalFilters];

    return {
        filterOptions,
        activeFilters,
        optionalFilters,
    };
}

export default function FilterContainer({
    children,
    showFilterMenu = true,
    showSavedFilters = true,
    dashboardSavedFilterState,
}) {
    const pageId = usePageId();
    const [filterState, filterStateActions] = useFilterState();
    const [urlState] = useUrlState();
    const [savedFilters, setSavedFilters] = useState(undefined);
    const [sharedFilters, setSharedFilters] = useState(undefined);
    const [defaultFilterState, setDefaultFilterState] = useState(undefined);
    const [partitionedFilters, setPartitionedFilters] = useState(undefined);
    const classes = useStyles();
    const [saveFiltersDialog, showSaveFiltersDialog] = useSaveFiltersDialog(filterState, savedFilters?.savedStates);
    const { enqueueSnackbar } = useSnackbar();
    const savedFiltersService = useSavedFiltersService();
    const { state: routerState } = useLocation();

    // Load saved/shared filters for the page.
    useEffect(() => {
        // Wait until the page ID is available before trying to load saved/shared filters.
        if (!pageId) return;

        // Clear previous filter state.
        filterStateActions.reset();

        // Clear previously partitioned filters.
        setPartitionedFilters(undefined);

        // Clear previously loaded saved filters.  This ensures any previous default state is not applied.
        setSavedFilters(undefined);
        setDefaultFilterState(undefined);

        Promise.all([savedFiltersService.get(pageId), savedFiltersService.getShared(pageId)])
            .then((results) => {
                const [saved, shared] = results;
                setSavedFilters(saved);
                setSharedFilters(shared);
                setDefaultFilterState(find([...saved.savedStates, ...shared.savedStates], 'default') || null);
            })
            .catch((e) => {
                setSavedFilters(undefined);
                setDefaultFilterState(null);
                log.error(e);
            });
    }, [pageId, savedFiltersService, filterStateActions]);

    useEffect(() => {
        // Wait until we have determined whether a default state exists before initializing the filter state.
        if (defaultFilterState === undefined) return;

        const encodedState = getEncodedFilterState(
            urlState,
            defaultFilterState,
            dashboardSavedFilterState,
            routerState?.navFromSidebar
        );
        const result = partitionFilters(Children.toArray(children), encodedState);
        const activeFilterIds = map(result.activeFilters, 'props.id');

        filterStateActions.initialize(encodedState, activeFilterIds, result.filterOptions);

        setPartitionedFilters(result);
        // This intentionally disables the exhaustive dependency check because we only want to partition filters when
        // either the URL filter state or default filter state changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlState, defaultFilterState]);

    useEffect(() => {
        // If filters are not yet initialized, or we haven't yet partitioned the initial filters, don't do anything,
        // since this effect is only meant to handle changes to the active filters resulting from dashboard editing.
        if (!isDashboard(pageId) || !partitionedFilters || !filterState) return;

        const encodedState = getEncodedFilterState(filterState.encoded, defaultFilterState, dashboardSavedFilterState);
        const result = partitionFilters(Children.toArray(children), encodedState);
        const currentActiveFilterIds = map(partitionedFilters.activeFilters, 'props.id');
        const newActiveFilterIds = map(result.activeFilters, 'props.id');
        const addedIds = difference(newActiveFilterIds, currentActiveFilterIds);
        const removedIds = difference(currentActiveFilterIds, newActiveFilterIds);

        if (addedIds.length > 0 || removedIds.length > 0) {
            filterStateActions.initialize(encodedState, newActiveFilterIds, result.filterOptions);
        }

        setPartitionedFilters(result);
        // This intentionally disables the exhaustive dependency check because we only want to partition filters when the
        // URL state or saved/shared filters change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [children]);

    useEffect(() => {
        // If filters are not yet initialized, or we haven't yet partitioned the initial filters, don't do anything,
        // since this effect is only meant to handle programmatic filter state changes AFTER filters have been
        // initialized.
        if (!partitionedFilters || !filterState) return;

        // Repartition the filters if the filter state changed and no longer matches the partitioned filters.
        // This can happen when the filter state is changed programmatically.
        const filterIds = Object.keys(filterState.current);
        const activeFilterIds = map(partitionedFilters.activeFilters, 'props.id');
        const addedFilterIds = difference(filterIds, activeFilterIds);
        const removedFilterIds = difference(activeFilterIds, filterIds);
        if (addedFilterIds.length > 0 || removedFilterIds.length > 0) {
            const { encoded: encodedState } = filterState;
            const result = partitionFilters(Children.toArray(children), encodedState);
            setPartitionedFilters(result);
        }
        // This intentionally disables the exhaustive dependency check because this effect both depends upon and changes the
        // partitioned filters.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState]);

    function addFilter(filter) {
        setPartitionedFilters({
            ...partitionedFilters,
            activeFilters: [...partitionedFilters.activeFilters, filter],
            optionalFilters: without(partitionedFilters.optionalFilters, filter),
        });
    }

    function handleSavedFilterStateSelected(savedFilterState) {
        const encodedState = savedFilterState.encodedFilterState || encodeState(savedFilterState.filterState);
        const result = partitionFilters(Children.toArray(children), encodedState);

        filterStateActions.initialize(encodedState);

        setPartitionedFilters(result);
    }

    function handleSaveFilterState() {
        showSaveFiltersDialog((newSavedState) => {
            // Clear any previous default if the new state is marked as default.
            const savedStates = savedFilters.savedStates.map((savedState) => {
                return newSavedState.default
                    ? {
                          ...savedState,
                          default: false,
                      }
                    : savedState;
            });

            savedFiltersService
                .save(pageId, {
                    ...savedFilters,
                    savedStates: [...savedStates, newSavedState],
                })
                .then((newlySavedFilters) => {
                    setSavedFilters(newlySavedFilters);
                    enqueueSnackbar(i18n._(t`Your filters were successfully saved`));
                });
        });
    }

    function handleDeleteFilterState(savedState) {
        savedFiltersService
            .save(pageId, {
                ...savedFilters,
                savedStates: without(savedFilters.savedStates, savedState),
            })
            .then((newlySavedFilters) => {
                setSavedFilters(newlySavedFilters);
                enqueueSnackbar(i18n._(t`Your filters were successfully deleted`));
            });
    }

    function handleToggleDefaultFilterState(savedStateToToggle) {
        savedFiltersService
            .save(pageId, {
                ...savedFilters,
                savedStates: savedFilters.savedStates.map((savedState) => {
                    return {
                        ...savedState,
                        // eslint-disable-next-line no-underscore-dangle
                        default: savedState._id === savedStateToToggle._id ? !savedState.default : false,
                    };
                }),
            })
            .then((newlySavedFilters) => {
                setSavedFilters(newlySavedFilters);
                enqueueSnackbar(i18n._(t`Your filters were successfully updated`));
            });
    }

    // Don't render anything if there aren't any filters (children).
    if (children.length === 0) return null;

    return (
        <TimeRangeHistoryProvider>
            <Toolbar id="filter-container" className={classes.root}>
                {showFilterMenu && (
                    <FilterMenu
                        optionalFilters={partitionedFilters?.optionalFilters || []}
                        savedFilterStates={showSavedFilters ? savedFilters?.savedStates : []}
                        sharedFilterStates={sharedFilters?.savedStates}
                        dashboardSavedFilterState={dashboardSavedFilterState}
                        onOptionalFilterSelected={addFilter}
                        onSavedFilterStateSelected={handleSavedFilterStateSelected}
                        onSaveFilterState={handleSaveFilterState}
                        onDeleteFilterState={handleDeleteFilterState}
                        onToggleDefaultFilterState={handleToggleDefaultFilterState}
                        showSavedFiltersProp={showSavedFilters}
                    />
                )}
                <div className={classes.filterContainer}>
                    {!!filterState &&
                        partitionedFilters?.activeFilters?.map((filter) => {
                            return (
                                <PerformanceLog key={filter.props.id} label={`${filter.type.name}:${filter.props.id}`}>
                                    <Filter>{filter}</Filter>
                                </PerformanceLog>
                            );
                        })}
                </div>
                {saveFiltersDialog}
            </Toolbar>
        </TimeRangeHistoryProvider>
    );
}

FilterContainer.propTypes = {
    children: node,
};

FilterContainer.defaultProps = {
    children: undefined,
};
